/**
 * Sprint 8 - User & permission management page (admin only).
 *
 * Features:
 *  - Table of company users with role/tier/parent
 *  - Create/edit user modal (validates parent per role rules)
 *  - Password reset (manual or auto-generate)
 *  - Soft delete (deactivate) with safeguards (cannot delete last admin, cannot self-delete)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, PencilSimple, Key, Trash, Prohibit, CheckCircle, Users as UsersIcon, Copy, X } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { backdropClick, stopBackdropPropagation } from '../lib/modalBackdrop';

const API = process.env.REACT_APP_BACKEND_URL;

// Defensive JSON reader to avoid "body stream already read" errors caused by
// browser extensions / dev-tools / service-workers that consume the Response
// before our handler can.
async function safeJson(res) {
  try {
    const text = await res.text();
    return text ? JSON.parse(text) : {};
  } catch (e) {
    console.warn('safeJson parse error', e);
    return {};
  }
}

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Amministratore' },
  { value: 'back_office', label: 'Back Office' },
  { value: 'commerciale', label: 'Commerciale' },
];
const TIER_OPTIONS = [
  { value: 'canale_vendita', label: 'Canale Vendita (vede agenti + sub-agenti)' },
  { value: 'agente', label: 'Agente (vede suoi sub-agenti)' },
  { value: 'sub_agente', label: 'Sub-agente (solo propri lead)' },
];

const defaultForm = {
  email: '', password: '', full_name: '',
  role: 'commerciale', sales_tier: 'sub_agente', parent_user_id: '', active: true,
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {mode: 'create'|'edit', form, uid?}
  const [resetModal, setResetModal] = useState(null); // {user, generated?}
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [resU, resH] = await Promise.all([
        fetch(`${API}/api/users`, { credentials: 'include' }),
        fetch(`${API}/api/users/hierarchy-options`, { credentials: 'include' }),
      ]);
      if (resU.ok) setUsers(await safeJson(resU));
      if (resH.ok) setHierarchy(await safeJson(resH));
    } catch {
      toast.error('Errore caricamento utenti');
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const openCreate = () => setModal({ mode: 'create', form: { ...defaultForm }, uid: null });
  const openEdit = u => setModal({
    mode: 'edit',
    uid: u.id,
    form: { ...u, password: '', parent_user_id: u.parent_user_id || '' },
  });

  const save = async () => {
    if (!modal) return;
    const f = modal.form;
    if (!f.email || (modal.mode === 'create' && !f.password) || !f.role) {
      toast.error('Email, password e ruolo sono obbligatori');
      return;
    }
    if (f.role === 'commerciale' && !f.sales_tier) {
      toast.error('Seleziona il livello del commerciale (tier)');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        email: f.email,
        full_name: f.full_name || '',
        role: f.role,
        sales_tier: f.role === 'commerciale' ? f.sales_tier : null,
        parent_user_id: f.parent_user_id || null,
      };
      if (modal.mode === 'create') payload.password = f.password;
      if (modal.mode === 'edit') payload.active = f.active;
      const url = modal.mode === 'create' ? `${API}/api/users` : `${API}/api/users/${modal.uid}`;
      const method = modal.mode === 'create' ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await safeJson(res);
      if (!res.ok) { toast.error(data.detail || 'Errore'); return; }
      toast.success(modal.mode === 'create' ? 'Utente creato' : 'Utente aggiornato');
      setModal(null);
      await reload();
    } finally { setSaving(false); }
  };

  const doGeneratePassword = async (u) => {
    const res = await fetch(`${API}/api/users/${u.id}/generate-password`, {
      method: 'POST', credentials: 'include',
    });
    const data = await safeJson(res);
    if (!res.ok) { toast.error(data.detail || 'Errore'); return; }
    setResetModal({ user: u, generated: data.password });
  };

  const doSetPassword = async (u, pw) => {
    if (!pw || pw.length < 6) { toast.error('Password min 6 caratteri'); return; }
    const res = await fetch(`${API}/api/users/${u.id}/reset-password`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: pw }),
    });
    const data = await safeJson(res);
    if (!res.ok) { toast.error(data.detail || 'Errore'); return; }
    toast.success('Password aggiornata');
    setResetModal(null);
  };

  const doDeactivate = async (u) => {
    if (!window.confirm(`Disattivare l'utente ${u.email}? Non potrà più accedere.`)) return;
    const res = await fetch(`${API}/api/users/${u.id}`, {
      method: 'DELETE', credentials: 'include',
    });
    const data = await safeJson(res);
    if (!res.ok) { toast.error(data.detail || 'Errore'); return; }
    toast.success('Utente disattivato');
    await reload();
  };

  const doReactivate = async (u) => {
    const res = await fetch(`${API}/api/users/${u.id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: true }),
    });
    const data = await safeJson(res);
    if (!res.ok) { toast.error(data.detail || 'Errore'); return; }
    toast.success('Utente riattivato');
    await reload();
  };

  const parentOptions = useMemo(() => {
    if (!modal) return [];
    const role = modal.form.role;
    const tier = modal.form.sales_tier;
    const excludeSelf = id => id !== modal.uid;
    if (role !== 'commerciale') return [];
    // Agente → parent is canale_vendita. Sub-agente → parent is agente or canale_vendita.
    if (tier === 'sub_agente') return hierarchy.filter(h => h.role === 'commerciale' && h.tier !== 'sub_agente' && excludeSelf(h.id));
    if (tier === 'agente') return hierarchy.filter(h => h.role === 'commerciale' && h.tier === 'canale_vendita' && excludeSelf(h.id));
    return []; // canale_vendita has no parent
  }, [modal, hierarchy]);

  if (loading) return <div className="p-8 text-slate-500">Caricamento…</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <UsersIcon size={28} className="text-orange-600" /> Utenti & Permessi
          </h1>
          <p className="text-sm text-slate-500 mt-1">Gestione utenti della tua azienda, ruoli e gerarchia commerciale</p>
        </div>
        <Button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="create-user-btn">
          <Plus size={16} className="mr-1" /> Nuovo utente
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2 font-medium text-slate-600">Email / Nome</th>
              <th className="text-left px-4 py-2 font-medium text-slate-600">Ruolo</th>
              <th className="text-left px-4 py-2 font-medium text-slate-600">Livello</th>
              <th className="text-left px-4 py-2 font-medium text-slate-600">Riporta a</th>
              <th className="text-left px-4 py-2 font-medium text-slate-600">Stato</th>
              <th className="text-right px-4 py-2 font-medium text-slate-600">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const parent = hierarchy.find(h => h.id === u.parent_user_id);
              return (
                <tr key={u.id} className={`border-b border-slate-100 hover:bg-slate-50 ${!u.active ? 'opacity-50' : ''}`} data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{u.email}</div>
                    {u.full_name && <div className="text-xs text-slate-500">{u.full_name}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                      {ROLE_OPTIONS.find(r => r.value === u.role)?.label || u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {u.sales_tier ? TIER_OPTIONS.find(t => t.value === u.sales_tier)?.label.split(' (')[0] : '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {parent ? parent.label : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {u.active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-50 text-green-700 text-xs">
                        <CheckCircle size={12} weight="fill" /> Attivo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 text-slate-500 text-xs">
                        <Prohibit size={12} weight="fill" /> Disattivato
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <button onClick={() => openEdit(u)} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Modifica" data-testid={`edit-user-${u.id}`}>
                      <PencilSimple size={14} />
                    </button>
                    <button onClick={() => setResetModal({ user: u })} className="p-1.5 rounded hover:bg-orange-100 text-orange-700" title="Reset password" data-testid={`reset-password-${u.id}`}>
                      <Key size={14} />
                    </button>
                    {u.active ? (
                      u.id !== currentUser?.id && (
                        <button onClick={() => doDeactivate(u)} className="p-1.5 rounded hover:bg-red-100 text-red-600" title="Disattiva" data-testid={`deactivate-${u.id}`}>
                          <Trash size={14} />
                        </button>
                      )
                    ) : (
                      <button onClick={() => doReactivate(u)} className="p-1.5 rounded hover:bg-green-100 text-green-700" title="Riattiva" data-testid={`reactivate-${u.id}`}>
                        <CheckCircle size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {users.length === 0 && <div className="text-center text-slate-500 py-10">Nessun utente. Crea il primo.</div>}
      </div>

      {/* Info box gerarchia */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-900">
        <div className="font-semibold mb-1">📖 Come funziona la gerarchia commerciale:</div>
        <ul className="list-disc pl-5 space-y-0.5">
          <li><strong>Canale Vendita</strong>: vede se stesso + tutti gli Agenti sottoposti + tutti i Sub-agenti di quegli Agenti</li>
          <li><strong>Agente</strong>: vede se stesso + i propri Sub-agenti diretti</li>
          <li><strong>Sub-agente</strong>: vede solo i lead che ha creato lui</li>
          <li><strong>Back Office</strong>: vede tutto il reparto di back office (contratti firmati, compliance, dogane, dichiarazioni)</li>
          <li><strong>Amministratore</strong>: accesso completo all'applicazione</li>
        </ul>
      </div>

      {/* Create/Edit Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" {...backdropClick(() => setModal(null), saving)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6" {...stopBackdropPropagation} data-testid="user-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">{modal.mode === 'create' ? 'Nuovo utente' : 'Modifica utente'}</h2>
              <button onClick={() => setModal(null)} disabled={saving}><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-600">Email *</label>
                <Input value={modal.form.email} onChange={e => setModal(m => ({ ...m, form: { ...m.form, email: e.target.value } }))}
                  disabled={modal.mode === 'edit'} data-testid="user-email" />
              </div>
              <div>
                <label className="text-xs text-slate-600">Nome completo</label>
                <Input value={modal.form.full_name || ''} onChange={e => setModal(m => ({ ...m, form: { ...m.form, full_name: e.target.value } }))}
                  data-testid="user-name" />
              </div>
              {modal.mode === 'create' && (
                <div>
                  <label className="text-xs text-slate-600">Password iniziale *</label>
                  <Input type="password" value={modal.form.password} onChange={e => setModal(m => ({ ...m, form: { ...m.form, password: e.target.value } }))}
                    data-testid="user-password" />
                  <p className="text-[10px] text-slate-400 mt-1">Min 6 caratteri. L'utente potrà cambiarla dopo.</p>
                </div>
              )}
              <div>
                <label className="text-xs text-slate-600">Ruolo *</label>
                <select value={modal.form.role} onChange={e => setModal(m => ({ ...m, form: { ...m.form, role: e.target.value, sales_tier: e.target.value === 'commerciale' ? (m.form.sales_tier || 'sub_agente') : null, parent_user_id: '' } }))}
                  className="w-full text-sm border rounded-md px-3 py-2 bg-white" data-testid="user-role">
                  {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {modal.form.role === 'commerciale' && (
                <>
                  <div>
                    <label className="text-xs text-slate-600">Livello commerciale *</label>
                    <select value={modal.form.sales_tier || 'sub_agente'} onChange={e => setModal(m => ({ ...m, form: { ...m.form, sales_tier: e.target.value, parent_user_id: '' } }))}
                      className="w-full text-sm border rounded-md px-3 py-2 bg-white" data-testid="user-tier">
                      {TIER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  {modal.form.sales_tier !== 'canale_vendita' && (
                    <div>
                      <label className="text-xs text-slate-600">Riporta a (superiore diretto)</label>
                      <select value={modal.form.parent_user_id || ''} onChange={e => setModal(m => ({ ...m, form: { ...m.form, parent_user_id: e.target.value } }))}
                        className="w-full text-sm border rounded-md px-3 py-2 bg-white" data-testid="user-parent">
                        <option value="">— Nessun superiore —</option>
                        {parentOptions.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                      </select>
                      {parentOptions.length === 0 && modal.form.sales_tier === 'agente' && (
                        <p className="text-[10px] text-amber-600 mt-1">Non ci sono ancora Canali Vendita. Crea prima un Canale Vendita, poi potrai assegnargli l'Agente.</p>
                      )}
                    </div>
                  )}
                </>
              )}
              {modal.mode === 'edit' && (
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={modal.form.active !== false} onChange={e => setModal(m => ({ ...m, form: { ...m.form, active: e.target.checked } }))} id="user-active" />
                  <label htmlFor="user-active" className="text-sm text-slate-700">Utente attivo</label>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setModal(null)} disabled={saving}>Annulla</Button>
              <Button onClick={save} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="save-user-btn">
                {saving ? 'Salvataggio…' : 'Salva'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" {...backdropClick(() => setResetModal(null))}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" {...stopBackdropPropagation}>
            <h2 className="text-lg font-semibold mb-4">Reset password — {resetModal.user.email}</h2>
            {resetModal.generated ? (
              <div>
                <p className="text-sm text-slate-600 mb-2">Password generata:</p>
                <div className="bg-slate-100 font-mono text-sm p-3 rounded flex items-center justify-between">
                  <span data-testid="generated-password">{resetModal.generated}</span>
                  <button onClick={() => { navigator.clipboard.writeText(resetModal.generated); toast.success('Copiata!'); }} className="p-1 hover:bg-white rounded">
                    <Copy size={14} />
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 mt-2">⚠ Salvala subito: non la vedrai più. Comunicala all'utente tramite canale sicuro.</p>
                <div className="flex justify-end mt-4">
                  <Button onClick={() => setResetModal(null)} className="bg-orange-600 hover:bg-orange-700 text-white">Chiudi</Button>
                </div>
              </div>
            ) : (
              <ManualPasswordForm onCancel={() => setResetModal(null)} onSet={(pw) => doSetPassword(resetModal.user, pw)} onGenerate={() => doGeneratePassword(resetModal.user)} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ManualPasswordForm({ onCancel, onSet, onGenerate }) {
  const [pw, setPw] = useState('');
  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs text-slate-600">Nuova password (min 6)</label>
        <Input type="text" value={pw} onChange={e => setPw(e.target.value)} data-testid="new-password" />
      </div>
      <div className="flex justify-between items-center gap-2 pt-2">
        <Button variant="outline" onClick={onGenerate} data-testid="generate-password-btn">🎲 Genera casuale</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>Annulla</Button>
          <Button onClick={() => onSet(pw)} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="set-password-btn">Imposta</Button>
        </div>
      </div>
    </div>
  );
}
